/** @jsx jsx */
import { Box, Container } from "@eworkit/iris";
import { FC, memo, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useRouter } from "router";
import { jsx } from "theme-ui";
import { ensure, stringType, TType } from "types";

import { ResponsiveCard } from "@/components/ResponsiveCard";
import { UserContext } from "@/components/User";
import { IURLSearchParams, IURLSortConfig, urlSearchParamsConfig } from "@/core/url";
import {
  fetchJobRequestListV1,
  fetchPublicJobRequestList,
  IJobRequest,
  IJobRequestURLFilter,
  IPageableJobRequest,
} from "@/data/job-requests";
import { Col, Flex } from "@/eworkitComponents";
import { TPageableFetchFunction, useMetaPageableFetcher } from "@/hooks/http";
import { Infobox } from "@/views/components/Page";

import { JobRequestsListRenderer } from "../../JobRequests/List/components";

interface IClientPageJobsTabProps {
  publicProfileIds: number[];
  isPublic?: boolean;
}

interface IClientJobRequestListSearchParams extends IURLSearchParams {
  tab: string;
}

export const configClientJobRequestListSearchParams: TType<IClientJobRequestListSearchParams> = {
  ...urlSearchParamsConfig,
  tab: stringType("jobs"),
};

export const clientJobRequestListSearchParamsType = ensure(configClientJobRequestListSearchParams);

export const ClientPageJobsTab: FC<IClientPageJobsTabProps> = memo(({ isPublic, publicProfileIds }) => {
  const { t } = useTranslation("public");
  const { contextCompany } = useContext(UserContext);

  const jobFetchFunction = isPublic ? fetchPublicJobRequestList : fetchJobRequestListV1;
  const { searchParams: meta, replaceSearchParams: changeMeta } = useRouter({
    ensureSearchParams: clientJobRequestListSearchParamsType,
  });
  const fetchJobs: TPageableFetchFunction<IJobRequest, IPageableJobRequest, IJobRequestURLFilter, IURLSortConfig[]> =
    useCallback(
      ({ filtersConfig, ...restParams }, options) => {
        return jobFetchFunction(
          {
            ...restParams,
            filtersConfig: {
              ...filtersConfig,
              publicProfileIds,
            },
            size: 10,
            companyId: contextCompany?.id,
          },
          options,
        );
      },
      [publicProfileIds, contextCompany],
    );

  const jobsFetcherState = useMetaPageableFetcher(fetchJobs, { meta, changeMeta }, [
    meta,
    changeMeta,
    publicProfileIds,
  ]);

  return (
    <Container>
      <Box px="sm">
        <Col width={{ xxs: 12 }}>
          <Flex direction="column" innerMargin="lg">
            <JobRequestsListRenderer
              {...jobsFetcherState}
              recordVariant="short"
              trackingSource="Client Jobs"
              renderEmpty={() => (
                <ResponsiveCard>
                  <Box sx={{ py: 72 }}>
                    <Infobox heading={t("CLIENT_PAGE.JOBS.NO_JOBS")} />
                  </Box>
                </ResponsiveCard>
              )}
            />
          </Flex>
        </Col>
      </Box>
    </Container>
  );
});
